import React, { useMemo, useState } from 'react';
import { UserRound, Loader2, IndianRupee } from 'lucide-react';
import SendPaymentLinkModal from './SendPaymentLinkModal';
import { WhatsAppConversation } from '@/services/integrations/whatsappInboxService';
import LeadDetailsSidebar from '@/components/LeadDetails/LeadDetailsSidebar';
import { getLeadDetails } from '@/actions/lead-manager';
import { Lead } from '@/types/leads/Table';
import { useUsers } from '@/contexts/UsersContext';
import { toastMessage } from '@/utils/toast-message';
import { getInitials, avatarColorFor, sessionWindowInfo, LEAD_BUCKET_BADGE } from '@/utils/whatsappInbox';
import { displayName } from '@/utils/displayName';

interface ConversationHeaderProps {
  conversation: WhatsAppConversation;
  /** Refresh the thread after something was sent from the header (a payment link). */
  onSent?: () => void;
}

const ConversationHeader: React.FC<ConversationHeaderProps> = ({ conversation: c, onSent }) => {
  const name = displayName(c.lead_name);
  const avatar = avatarColorFor(c.wa_phone || name || String(c.id));
  const bucket = c.lead_bucket ? LEAD_BUCKET_BADGE[c.lead_bucket] : null;
  const win = c.status === 'open' && !c.opted_out ? sessionWindowInfo(c.last_inbound_at) : null;

  // Open the full customer panel (edit details, dispositions, timeline, orders) over
  // the Inbox -- the same panel the lead screens use -- without leaving the chat.
  const { activeUsers } = useUsers();
  const usersForDropdown = useMemo(() => activeUsers.map((u) => ({
    value: u.uuid, label: u.userName, email: u.userEmail, role: u.role?.role_name,
  })), [activeUsers]);
  const [lead, setLead] = useState<Lead | null>(null);
  const [showPayLink, setShowPayLink] = useState(false);
  const [opening, setOpening] = useState(false);
  const openCustomer = async () => {
    if (!c.lead_uuid) return;
    setOpening(true);
    try {
      const res = await getLeadDetails(c.lead_uuid);
      if (res.success && res.data) setLead(res.data as Lead);
      else toastMessage('Could not open this customer', 'error');
    } catch {
      toastMessage('Could not open this customer', 'error');
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="h-[66px] flex-shrink-0 bg-white border-b border-gray-200 flex items-center px-5 gap-3">
      <div className={`w-[38px] h-[38px] rounded-full flex items-center justify-center text-[13px] font-bold flex-shrink-0 ${avatar.bg} ${avatar.text}`}>
        {getInitials(name || c.wa_phone)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[14.5px] font-bold truncate">{name || c.wa_phone || 'Unknown'}</div>
          {bucket && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${bucket.bg} ${bucket.text}`}>
              {bucket.label}
            </span>
          )}
        </div>
        <div className="text-[11.5px] text-gray-500 mt-0.5 truncate">
          {c.wa_phone}
          {c.owner_name && <> · Owner {c.owner_name}</>}
        </div>
      </div>

      {c.lead_uuid && !c.opted_out && (
        <button
          onClick={() => setShowPayLink(true)}
          title="Send a Razorpay payment link, or a draft order's checkout (invoice) link, for one of this customer's orders"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 flex-shrink-0"
        >
          <IndianRupee className="w-3.5 h-3.5" />
          Payment / checkout link
        </button>
      )}

      {c.lead_uuid && (
        <button
          onClick={openCustomer}
          disabled={opening}
          title="Open the customer: edit details, disposition, timeline, orders"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 flex-shrink-0 disabled:opacity-50"
        >
          {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserRound className="w-3.5 h-3.5" />}
          Open customer
        </button>
      )}

      {win && (
        <div className={`hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg flex-shrink-0 ${
          win.open ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className={`w-[7px] h-[7px] rounded-full ${win.open ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          <div className={`text-[11.5px] font-bold ${win.open ? 'text-green-800' : 'text-amber-800'}`}>
            {win.open ? `Free-form open · ${win.label}` : 'Window closed'}
          </div>
        </div>
      )}

      {showPayLink && c.lead_uuid && (
        <SendPaymentLinkModal
          leadUuid={c.lead_uuid}
          customerName={name || c.wa_phone || 'the customer'}
          onClose={() => setShowPayLink(false)}
          onSent={() => onSent?.()}
        />
      )}

      {lead && (
        <LeadDetailsSidebar
          isOpen={!!lead}
          onClose={() => setLead(null)}
          lead={lead}
          activeUsersForDropdown={usersForDropdown}
          onUpdate={async () => {
            const res = await getLeadDetails(lead.uuid);
            if (res.success && res.data) setLead(res.data as Lead);
          }}
        />
      )}
    </div>
  );
};

export default ConversationHeader;
